import {
  findToolPart,
  getProcessingToolPart,
  getStrategyIntegrationMessageIndex,
  hasCompletedStrategyToolInMessages,
  isArtQueueToolPartType,
  findLatestArtQueueToolPart,
  buildArtQueueProgressSteps,
  getExpectedArtCount,
  isStrategyToolPartType,
  isFullStrategyPipelineToolType,
  isStrategyDocumentOnlyToolType,
  getStrategyToolNameFromPartType,
  getPostQuantityFromMessages,
} from '../domain/toolPartSelectors';
import {
  buildQueueStepsFromProgress,
  buildDocumentOnlyStrategySteps,
  buildRegenStrategyQueueStepsFromDefaults,
} from '../hooks/campaignSocket/constants';
import {CAMPAIGN_WIDGET_TOOL_NAMES} from './campaignCompletionMessages';
import {resolveSchedulingProgressProps} from './schedulingProgressHelpers';
import {
  findCampaignTypeToolPart,
  getCampaignTypeToolTrackingId,
  getToolTrackingId as getParameterizationToolTrackingId,
  isCampaignTypeToolPartType,
  isParameterizationToolPartType,
  isWidgetToolPartType,
  PALETTE_UI_TOOL_NAME,
} from '../domain/campaignToolCatalog';

export function isStrategyToolType(toolType) {
  return isStrategyToolPartType(toolType);
}

const STRATEGY_OPERATION_TOOL_NAMES = new Set([
  'gerar_documento_estrategia',
  'editar_documento_estrategia',
  'regerar_documento_estrategia',
]);

const ART_OPERATION_TOOL_NAMES = new Set([
  'gerar_artes_campanha',
  'regerar_artes_campanha',
  'editar_arte_campanha',
  'editar_artes_lote',
]);

function getOperationKindFromToolName(toolName) {
  if (!toolName) {
    return null;
  }

  if (STRATEGY_OPERATION_TOOL_NAMES.has(toolName)) {
    return 'strategy';
  }

  if (ART_OPERATION_TOOL_NAMES.has(toolName)) {
    return 'arts';
  }

  return null;
}

const SYNC_WIDGET_MAP = {
  [`tool-${CAMPAIGN_WIDGET_TOOL_NAMES.QUANTIDADE_ARTES}`]: {
    key: 'postQuantity',
    buildProps: (output, part, isAwaiting) => ({
      selectedValue: output.selectedValue ?? null,
      availableArts: output.availableArts ?? null,
      isReadOnly: !isAwaiting,
      toolCallId: output.trackingId || part.toolCallId,
    }),
  },
  [`tool-${CAMPAIGN_WIDGET_TOOL_NAMES.IMAGENS_CAMPANHA}`]: {
    key: 'productImages',
    buildProps: (output, part, isAwaiting) => ({
      imageUrls: output.imageUrls ?? [],
      isReadOnly: !isAwaiting,
      toolCallId: output.trackingId || part.toolCallId,
    }),
  },
  [`tool-${CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ESTRATEGIA}`]: {
    key: 'strategyApproval',
    buildProps: (output, part, isAwaiting) => ({
      isReadOnly: !isAwaiting,
      toolCallId: output.trackingId || part.toolCallId,
    }),
  },
  [`tool-${CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ARTES}`]: {
    key: 'artsApproval',
    buildProps: (output, part, isAwaiting) => ({
      isReadOnly: !isAwaiting,
      toolCallId: output.trackingId || part.toolCallId,
    }),
  },
  [`tool-${CAMPAIGN_WIDGET_TOOL_NAMES.MODO_CONCLUSAO}`]: {
    key: 'completionMode',
    buildProps: (output, part, isAwaiting) => ({
      isReadOnly: !isAwaiting,
      toolCallId: output.trackingId || part.toolCallId,
    }),
  },
  [`tool-${CAMPAIGN_WIDGET_TOOL_NAMES.CONFIRMAR_EDICAO_LOTE}`]: {
    key: 'batchEditConfirm',
    buildProps: (output, part, isAwaiting) => ({
      isReadOnly: !isAwaiting,
      toolCallId: output.trackingId || part.toolCallId,
      requiredArts: output.requiredArts ?? null,
      question: output.question ?? null,
    }),
  },
  [`tool-${CAMPAIGN_WIDGET_TOOL_NAMES.CONFIRMAR_REJEICAO_LOTE}`]: {
    key: 'batchRejectConfirm',
    buildProps: (output, part, isAwaiting) => ({
      isReadOnly: !isAwaiting,
      toolCallId: output.trackingId || part.toolCallId,
      requiredArts: output.requiredArts ?? null,
      question: output.question ?? null,
    }),
  },
  [`tool-${CAMPAIGN_WIDGET_TOOL_NAMES.ACOES_FINAIS}`]: {
    key: 'completionActions',
    buildProps: (output, part, isAwaiting) => ({
      isReadOnly:
        !isAwaiting ||
        output?.status === 'completed' ||
        output?.action === 'FINISH',
      toolCallId: output.trackingId || part.toolCallId,
    }),
  },
  [`tool-${CAMPAIGN_WIDGET_TOOL_NAMES.REDES_SOCIAIS}`]: {
    key: 'socialMedia',
    buildProps: (output, part, isAwaiting) => ({
      networks: output.networks ?? [],
      isReadOnly: !isAwaiting,
      toolCallId: output.trackingId || part.toolCallId,
    }),
  },
  [`tool-${CAMPAIGN_WIDGET_TOOL_NAMES.CONFIGURAR_AGENDAMENTO}`]: {
    key: 'scheduleConfig',
    buildProps: (output, part, isAwaiting) => ({
      arts: output.arts ?? output.scheduledArtsPreview ?? [],
      isReadOnly: !isAwaiting,
      toolCallId: output.trackingId || part.toolCallId,
    }),
  },
};

function resolveStrategyProgressSteps(queueSteps, messages) {
  if (!hasCompletedStrategyToolInMessages(messages)) {
    return queueSteps;
  }

  const allComplete =
    Array.isArray(queueSteps) &&
    queueSteps.length > 0 &&
    queueSteps.every((step) => step.status === 'complete');

  if (allComplete) {
    return queueSteps;
  }

  return queueSteps.map((step) => ({...step, status: 'complete'}));
}

function findLatestStrategyToolPart(messages) {
  if (!Array.isArray(messages)) {
    return null;
  }

  let latestPart = null;

  messages.forEach((msg) => {
    (msg.parts || []).forEach((part) => {
      if (
        isStrategyToolPartType(part.type) &&
        part.state === 'output-available' &&
        ['processing', 'completed', 'error'].includes(part.output?.status)
      ) {
        latestPart = part;
      }
    });
  });

  return latestPart;
}

function isLatestStrategyToolPart(messages, part) {
  const latestPart = findLatestStrategyToolPart(messages);
  if (!latestPart || !part) {
    return false;
  }

  const partId = part.output?.trackingId || part.toolCallId;
  const latestId = latestPart.output?.trackingId || latestPart.toolCallId;
  return partId === latestId;
}

function resolveStrategyProgressStepsForPart(part, context = {}) {
  const {
    queueSteps = [],
    initialQueueSteps = [],
    messages = [],
    strategyProgressTitle,
    strategyDocumentEditTitle,
    strategyRegenQueueStepsTemplate = [],
  } = context;
  const status = part?.output?.status;
  const toolName = getStrategyToolNameFromPartType(part?.type);

  if (toolName === 'editar_documento_estrategia') {
    const title = strategyDocumentEditTitle || strategyProgressTitle;
    if (status === 'completed') {
      return buildDocumentOnlyStrategySteps(title, 'complete');
    }
    if (status === 'error') {
      return buildDocumentOnlyStrategySteps(title, 'idle');
    }
    return buildDocumentOnlyStrategySteps(title, 'pending');
  }

  if (toolName === 'regerar_documento_estrategia') {
    const regenSteps =
      strategyRegenQueueStepsTemplate.length > 0
        ? strategyRegenQueueStepsTemplate
        : buildRegenStrategyQueueStepsFromDefaults();
    if (status === 'completed') {
      return regenSteps.map((step) => ({...step, status: 'complete'}));
    }
    if (status === 'error') {
      return regenSteps;
    }
    const currentStep = part.output?.currentStep ?? 0;
    return buildQueueStepsFromProgress(currentStep, regenSteps);
  }

  if (isStrategyDocumentOnlyToolType(part?.type)) {
    const title = strategyDocumentEditTitle || strategyProgressTitle;
    if (status === 'completed') {
      return buildDocumentOnlyStrategySteps(title, 'complete');
    }
    if (status === 'error') {
      return buildDocumentOnlyStrategySteps(title, 'idle');
    }
    return buildDocumentOnlyStrategySteps(title, 'pending');
  }

  const baseSteps = initialQueueSteps.length > 0 ? initialQueueSteps : queueSteps;

  if (status === 'completed') {
    const steps = baseSteps.length > 0 ? baseSteps : queueSteps;
    return steps.map((step) => ({...step, status: 'complete'}));
  }

  if (status === 'error') {
    return baseSteps.length > 0 ? baseSteps : queueSteps;
  }

  if (status === 'processing') {
    const isLatest = isLatestStrategyToolPart(messages, part);
    if (
      isLatest &&
      Array.isArray(queueSteps) &&
      queueSteps.some(
        (step) => step.status === 'pending' || step.status === 'complete',
      )
    ) {
      return queueSteps;
    }

    const currentStep = part.output?.currentStep ?? 0;
    return buildQueueStepsFromProgress(currentStep, baseSteps);
  }

  return queueSteps;
}

function getActiveArtQueueProcessingPart(messages) {
  const processingPart = getProcessingToolPart(messages);
  if (processingPart && isArtQueueToolPartType(processingPart.type)) {
    return processingPart;
  }
  return null;
}

function shouldSuppressHistoricalArtQueueProgress(part, context = {}) {
  const activePart = getActiveArtQueueProcessingPart(context.messages || []);
  if (!activePart || activePart.output?.status !== 'processing') {
    return false;
  }

  const activeTrackingId =
    activePart.output?.trackingId || activePart.toolCallId || null;
  const partTrackingId = part?.output?.trackingId || part?.toolCallId || null;

  return Boolean(
    activeTrackingId &&
      partTrackingId &&
      activeTrackingId !== partTrackingId,
  );
}

function resolveArtQueueProgressTitle(part, context = {}) {
  const output = part?.output ?? {};
  if (output.stepName) {
    return output.stepName;
  }

  const toolName = part?.type?.replace(/^tool-/, '');
  if (toolName === 'editar_arte_campanha') {
    const pieceNumber = Number(output.pieceNumber) || 1;
    const label =
      context.formatArtPieceLabel?.(pieceNumber) ?? `Arte ${pieceNumber}`;
    return `Editando ${label}`;
  }

  if (toolName === 'editar_artes_lote') {
    return context.artProgressEditingTitle ?? context.artProgressTitle;
  }

  return context.artProgressTitle;
}

function buildArtQueueProgressIntegration(part, context = {}) {
  if (!isArtQueueToolPartType(part?.type)) {
    return null;
  }

  if (shouldSuppressHistoricalArtQueueProgress(part, context)) {
    return null;
  }

  const output = part?.output ?? {};
  const status = output.status;
  if (!['processing', 'completed', 'error'].includes(status)) {
    return null;
  }

  if (status === 'processing') {
    const toolName = part.type?.replace(/^tool-/, '');
    const isEditTool =
      toolName === 'editar_arte_campanha' || toolName === 'editar_artes_lote';
    if (!isEditTool) {
      return null;
    }

    const liveSteps =
      Array.isArray(context.artSteps) && context.artSteps.length > 0
        ? context.artSteps
        : null;
    if (!liveSteps) {
      return null;
    }

    const trackingId = part.output?.trackingId || part.toolCallId || null;
    return {
      key: 'artQueueProgress',
      trackingId,
      props: {
        steps: liveSteps,
        title: resolveArtQueueProgressTitle(part, context),
      },
    };
  }

  if (
    status === 'processing' &&
    (context.activeOperationFamily ??
      getOperationKindFromToolName(context.activeOperationToolName)) === 'strategy'
  ) {
    return null;
  }

  const trackingId = part.output?.trackingId || part.toolCallId || null;
  const expectedTotal = getExpectedArtCount(
    context.messages || [],
    context.sessionDetail,
  );
  const steps = buildArtQueueProgressSteps(part, {
    expectedTotal,
    formatPieceLabel: context.formatArtPieceLabel,
  });

  if (steps.length === 0) {
    return null;
  }

  return {
    key: 'artQueueProgress',
    trackingId,
    props: {
      steps,
      title: resolveArtQueueProgressTitle(part, context),
    },
  };
}

export function hasPersistedArtQueueProgress(messages) {
  return Boolean(findLatestArtQueueToolPart(messages));
}

function buildStrategyProgressIntegration(part, context = {}) {
  if (!isStrategyToolPartType(part.type)) {
    return null;
  }

  const status = part?.output?.status;
  if (!['processing', 'completed', 'error'].includes(status)) {
    return null;
  }

  if (status === 'processing') {
    return null;
  }

  if (
    status === 'processing' &&
    (context.activeOperationFamily ??
      getOperationKindFromToolName(context.activeOperationToolName)) === 'arts'
  ) {
    return null;
  }

  return {
    key: 'strategyProgress',
    props: {
      steps: resolveStrategyProgressStepsForPart(part, context),
      title:
        getStrategyToolNameFromPartType(part?.type) ===
        'editar_documento_estrategia'
          ? context.strategyDocumentEditTitle || context.strategyProgressTitle
          : context.strategyProgressTitle,
    },
  };
}

export function getLastAssistantMessageIndex(messages) {
  if (!Array.isArray(messages)) {
    return -1;
  }
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'assistant') {
      return i;
    }
  }
  return -1;
}

export function hasAwaitingStrategyApproval(messages) {
  return Boolean(
    findToolPart(messages, CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ESTRATEGIA, {
      statuses: ['awaiting_selection'],
    }),
  );
}

function hasResolvedStrategyApproval(messages, sessionMessages = []) {
  return Boolean(
    findToolPart(messages, CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ESTRATEGIA, {
      statuses: ['completed'],
    }) ||
      findToolPart(
        sessionMessages,
        CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ESTRATEGIA,
        {statuses: ['completed']},
      ),
  );
}

export function resolvePendingStrategyApprovalIntegration({
  messages,
  sessionMessages = [],
  campaignStatus,
}) {
  if (hasResolvedStrategyApproval(messages, sessionMessages)) {
    return null;
  }

  if (hasAwaitingStrategyApproval(messages)) {
    return null;
  }

  const strategyReady =
    isStrategyGenerationComplete(messages, campaignStatus) ||
    isStrategyGenerationComplete(sessionMessages, campaignStatus);

  if (!strategyReady) {
    return null;
  }

  const approvalPart = findToolPart(
    sessionMessages,
    CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ESTRATEGIA,
    {statuses: ['awaiting_selection']},
  );

  if (!approvalPart) {
    return null;
  }

  const postQuantity =
    getPostQuantityFromMessages(messages, {messages: sessionMessages}) ?? 5;

  return {
    key: 'strategyApproval',
    props: {
      isReadOnly: false,
      toolCallId: approvalPart.output?.trackingId || approvalPart.toolCallId,
      requiredArts: postQuantity,
    },
  };
}

export function isStrategyGenerationComplete(messages, campaignStatus) {
  if (hasCompletedStrategyToolInMessages(messages)) {
    return true;
  }

  return campaignStatus === 'strategy_generated';
}

export function isStrategyProgressActive({
  showQueueProgress,
  messages,
  isProcessing,
  campaignStatus,
  activeOperationToolName = null,
  activeOperationFamily = null,
}) {
  const family =
    activeOperationFamily ?? getOperationKindFromToolName(activeOperationToolName);

  if (family === 'arts') {
    return false;
  }

  if (hasAwaitingStrategyApproval(messages)) {
    return false;
  }

  if (getStrategyIntegrationMessageIndex(messages) >= 0) {
    return false;
  }

  if (isStrategyGenerationComplete(messages, campaignStatus)) {
    return false;
  }

  const processingPart = getProcessingToolPart(messages);
  if (isStrategyDocumentOnlyToolType(processingPart?.type)) {
    return false;
  }

  if (showQueueProgress) {
    return true;
  }

  if (!isProcessing) {
    return false;
  }

  return isFullStrategyPipelineToolType(processingPart?.type);
}

export function hasAwaitingArtsApproval(messages) {
  return Boolean(
    findToolPart(messages, CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ARTES, {
      statuses: ['awaiting_selection'],
    }),
  );
}

export function isArtQueueProgressActive({
  messages,
  artSteps,
  activeOperationToolName = null,
  activeOperationFamily = null,
}) {
  const family =
    activeOperationFamily ?? getOperationKindFromToolName(activeOperationToolName);

  if (family === 'strategy') {
    return false;
  }

  const processingPart = getActiveArtQueueProcessingPart(messages);
  if (processingPart) {
    if (!Array.isArray(artSteps) || artSteps.length === 0) {
      return false;
    }
    return artSteps.some((step) => step.status !== 'complete');
  }

  if (hasPersistedArtQueueProgress(messages)) {
    return false;
  }

  if (!Array.isArray(artSteps) || artSteps.length === 0) {
    return false;
  }

  return artSteps.some((step) => step.status !== 'complete');
}

function isInteractiveToolOutput(output) {
  return (
    output?.status === 'awaiting_selection' || output?.status === 'completed'
  );
}

function buildCampaignTypeIntegration(part) {
  const output = part?.output;
  if (!part || !isInteractiveToolOutput(output)) {
    return null;
  }

  const isAwaiting = output.status === 'awaiting_selection';

  return {
    key: 'campaignType',
    props: {
      selectedValue: output.selectedValue ?? null,
      isReadOnly: !isAwaiting,
      toolCallId: getCampaignTypeToolTrackingId(part),
    },
  };
}

function buildPaletteIntegration(part) {
  const output = part?.output;
  if (!part || !isInteractiveToolOutput(output)) {
    return null;
  }

  const isAwaiting = output.status === 'awaiting_selection';

  return {
    key: 'palette',
    props: {
      isReadOnly: !isAwaiting,
      toolCallId: getParameterizationToolTrackingId(part),
      paletteData: isAwaiting ? null : output,
    },
  };
}

const APPROVAL_INTEGRATION_KEYS = [
  'strategyApproval',
  'artsApproval',
  'completionMode',
  'batchEditConfirm',
  'batchRejectConfirm',
  'completionActions',
];

function buildSyncWidgetIntegration(part) {
  const config = SYNC_WIDGET_MAP[part.type];
  if (!config) {
    return null;
  }

  if (APPROVAL_INTEGRATION_KEYS.includes(config.key)) {
    return null;
  }

  const output = part?.output;
  if (!isInteractiveToolOutput(output)) {
    return null;
  }

  const isAwaiting = output.status === 'awaiting_selection';
  return {
    key: config.key,
    props: config.buildProps(output, part, isAwaiting),
  };
}

function isToolPartOutputReady(part) {
  if (!part?.type?.startsWith('tool-')) {
    return false;
  }
  if (part.state === 'output-available') {
    return true;
  }
  const status = part.output?.status;
  return status === 'awaiting_selection' || status === 'completed';
}

function buildSchedulingProgressIntegration(part, context = {}) {
  if (part.type !== `tool-${CAMPAIGN_WIDGET_TOOL_NAMES.EXECUTAR_AGENDAMENTO}`) {
    return null;
  }

  const props = resolveSchedulingProgressProps({
    part,
    schedulingLiveState: context.schedulingLiveState,
    sessionDetail: context.sessionDetail,
  });
  if (!props) {
    return null;
  }

  const trackingId = part.output?.trackingId || part.toolCallId || null;
  return {
    key: 'schedulingProgress',
    trackingId,
    props,
  };
}

function buildIntegrationForToolPart(part, context = {}) {
  if (!isToolPartOutputReady(part)) {
    return null;
  }

  if (isCampaignTypeToolPartType(part.type)) {
    return buildCampaignTypeIntegration(part);
  }

  if (part.type === `tool-${PALETTE_UI_TOOL_NAME}`) {
    return buildPaletteIntegration(part);
  }

  const syncWidget = buildSyncWidgetIntegration(part);
  if (syncWidget) {
    return syncWidget;
  }

  if (
    part.type === `tool-${CAMPAIGN_WIDGET_TOOL_NAMES.EXECUTAR_AGENDAMENTO}`
  ) {
    return buildSchedulingProgressIntegration(part, context);
  }

  return null;
}

export function resolveMessageIntegrations(message, context = {}) {
  if (!message || message.role !== 'assistant') {
    return [];
  }

  const integrations = [];

  for (const part of message.parts || []) {
    const strategyProgress = buildStrategyProgressIntegration(part, context);
    if (strategyProgress) {
      integrations.push(strategyProgress);
      continue;
    }

    const artQueueProgress = buildArtQueueProgressIntegration(part, context);
    if (artQueueProgress) {
      integrations.push(artQueueProgress);
      continue;
    }

    const integration = buildIntegrationForToolPart(part, context);
    if (integration) {
      if (integration.key === 'scheduleConfig' && context.artResults?.length) {
        integration.props.arts = context.artResults;
      }
      if (
        integration.key === 'scheduleConfig' &&
        context.onSchedulePreviewArt
      ) {
        integration.props.onPreviewArt = context.onSchedulePreviewArt;
      }
      if (
        (integration.key === 'artsApproval' ||
          integration.key === 'batchEditConfirm') &&
        context.artResults?.length
      ) {
        integration.props.requiredArts =
          integration.props.requiredArts ?? context.artResults.length;
      }
      if (integration.key === 'strategyApproval') {
        integration.props.requiredArts =
          getPostQuantityFromMessages(context.messages || [message]) ?? 5;
      }
      integrations.push(integration);
    }
  }

  const strategyApproved = message.parts?.some(
    (part) =>
      part.type ===
        `tool-${CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ESTRATEGIA}` &&
      part.output?.status === 'completed' &&
      part.output?.action === 'APPROVED',
  );

  if (strategyApproved && context.strategyMarkdown) {
    integrations.push({
      key: 'strategyDownloader',
      props: {
        markdown: context.strategyMarkdown,
        fileName: 'estrategia-campanha',
      },
    });
  }

  const showZip =
    message.parts?.some(
      (part) =>
        part.type === `tool-${CAMPAIGN_WIDGET_TOOL_NAMES.FINALIZAR_CAMPANHA}` &&
        part.output?.status === 'completed',
    ) ||
    message.parts?.some(
      (part) =>
        part.type === `tool-${CAMPAIGN_WIDGET_TOOL_NAMES.MODO_CONCLUSAO}` &&
        part.output?.status === 'completed' &&
        part.output?.mode === 'FINALIZE',
    );

  if (showZip && context.sessionId) {
    integrations.push({
      key: 'downloadZip',
      props: {sessionId: context.sessionId},
    });
  }

  return integrations;
}

export function deduplicateMessageIntegrations(perMessageIntegrations, anchorIntegrations) {
  if (!Array.isArray(anchorIntegrations) || anchorIntegrations.length === 0) {
    return [];
  }

  const persistedKeys = new Set(
    (perMessageIntegrations || []).map((integration) => integration.key),
  );
  const persistedArtTrackingIds = new Set(
    (perMessageIntegrations || [])
      .filter((integration) => integration.key === 'artQueueProgress')
      .map((integration) => integration.trackingId)
      .filter(Boolean),
  );

  return anchorIntegrations.filter((integration) => {
    if (persistedKeys.has(integration.key)) {
      return false;
    }

    if (
      integration.key === 'strategyProgress' &&
      persistedKeys.has('strategyProgress')
    ) {
      return false;
    }

    if (integration.key === 'artQueueProgress') {
      if (persistedKeys.has('artQueueProgress')) {
        return false;
      }
      if (
        integration.props?.trackingId &&
        persistedArtTrackingIds.has(integration.props.trackingId)
      ) {
        return false;
      }
    }

    return true;
  });
}

export function resolveActiveIntegrations({
  showQueueProgress,
  messages,
  sessionMessages,
  isProcessing,
  campaignStatus,
  activeOperationToolName = null,
  activeOperationFamily = null,
  queueSteps,
  strategyProgressTitle,
  artSteps,
  artProgressTitle,
}) {
  const activeIntegrations = [];

  if (
    isStrategyProgressActive({
      showQueueProgress,
      messages,
      isProcessing,
      campaignStatus,
      activeOperationToolName,
      activeOperationFamily,
    })
  ) {
    activeIntegrations.push({
      key: 'strategyProgress',
      props: {
        steps: resolveStrategyProgressSteps(queueSteps, messages),
        title: strategyProgressTitle,
      },
    });
  }

  const pendingStrategyApproval = resolvePendingStrategyApprovalIntegration({
    messages,
    sessionMessages,
    campaignStatus,
  });
  if (pendingStrategyApproval) {
    activeIntegrations.push(pendingStrategyApproval);
  }

  if (
    isArtQueueProgressActive({
      messages,
      artSteps,
      activeOperationToolName,
      activeOperationFamily,
    })
  ) {
    activeIntegrations.push({
      key: 'artQueueProgress',
      props: {
        steps: artSteps || [],
        title: artProgressTitle,
      },
    });
  }

  return activeIntegrations;
}

export function isCampaignTypeInputLocked(messages) {
  return Boolean(
    findCampaignTypeToolPart(messages, {statuses: ['awaiting_selection']}),
  );
}

export {isParameterizationInputLocked} from '../domain/campaignStateSelectors';

export function isInteractiveToolPartType(partType) {
  return (
    isCampaignTypeToolPartType(partType) ||
    isParameterizationToolPartType(partType) ||
    isWidgetToolPartType(partType)
  );
}
